<template>
    <div class="sl-photo-carousel">
        <!-- Photos -->
        <div
            v-if="photos.length === 0"
            class="sl-photo-carousel__photo sl-photo-carousel__photo--none"
        >
            <img src="/img/camera-off.svg" alt="" />
        </div>

        <div v-else class="sl-photo-carousel__photo">
            <!-- "Previous" Button -->
            <div
                v-if="photos.length > 1"
                class="sl-photo-carousel__nav sl-photo-carousel__nav--prev"
                :class="{ 'sl-photo-carousel__nav--disabled': current <= 0 }"
                @click.stop="previous"
            >
                <mdi-icon icon="mdiChevronRight" />
            </div>
            <div v-else></div>

            <img :src="photos[clampedCurrent]" alt="" :class="imageClass" />

            <!-- "Next" Button -->
            <div
                v-if="photos.length > 1"
                class="sl-photo-carousel__nav sl-photo-carousel__nav--next"
                :class="{ 'sl-photo-carousel__nav--disabled': current >= max }"
                @click.stop="next"
            >
                <mdi-icon icon="mdiChevronRight" />
            </div>
            <div v-else></div>
        </div>

        <!-- Bullets -->
        <div
            v-if="!noBullets && photos.length > 1"
            class="sl-photo-carousel__bullets"
        >
            <div
                v-for="i in photos.length"
                :key="i"
                class="sl-photo-carousel__bullet"
                :class="{ 'sl-photo-carousel__bullet--current': current === i - 1 }"
                @click.stop="current = i - 1"
            >
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    photos:
    {
        type:    Array,
        default: () => [],
    },

    imageClass:
    {
        type:    [Object, Array, String, null],
        default: null,
    },

    noBullets:
    {
        type:    Boolean,
        default: false,
    },
});

const current = ref(0);

const max = computed(() => Math.max(0, props.photos.length - 1));
const clampedCurrent = computed(() => Math.max(0, Math.min(current.value, max.value)));

function previous()
{
    current.value -= 1;
    if(current.value < 0)
    {
        current.value = 0;
    }
}

function next()
{
    current.value += 1;
    if(current.value > max.value)
    {
        current.value = max.value;
    }
}
</script>

<style scoped>
@reference "#main.css";

.sl-photo-carousel
{
    --arrow-size: theme('size.8');

    @apply flex flex-col overflow-hidden;
}

/* Photo */
.sl-photo-carousel__photo
{
    @apply flex justify-between items-center w-full;

    & > img
    {
        @apply object-cover;

        max-width: calc(100% - 2 * var(--arrow-size));
    }

    &.sl-photo-carousel__photo--none
    {
        @apply justify-center text-gray-200 fill-current;

        & > img
        {
            @apply w-[215px] h-[215px];
        }
    }
}

.sl-photo-carousel__photo--none
{
}

/* Previous & Next buttons */
.sl-photo-carousel__nav
{
    @apply cursor-pointer text-gray-300 select-none transition;

    & > .mdi-icon
    {
        width:  var(--arrow-size);
        height: var(--arrow-size);
    }
}

.sl-photo-carousel:hover .sl-photo-carousel__nav:not(.sl-photo-carousel__nav--disabled)
{
    @apply text-gray-500;
}

.sl-photo-carousel .sl-photo-carousel__nav:not(.sl-photo-carousel__nav--disabled):hover
{
    @apply text-primary-400;
}

.sl-photo-carousel__nav--prev
{
    & > .mdi-icon
    {
        @apply rotate-180;
    }
}

.sl-photo-carousel__nav--next
{
}

.sl-photo-carousel__nav--disabled
{
    @apply cursor-default text-gray-300;
}

/* Bullets */
.sl-photo-carousel__bullets
{
    @apply flex justify-center items-center gap-2 mt-2;
}

.sl-photo-carousel__bullet
{
    @apply cursor-pointer inline-block rounded-full border border-gray-300 w-3 h-3 bg-white transition;
}

.sl-photo-carousel__bullet--current,
.sl-photo-carousel__bullet:hover
{
    @apply border-white bg-primary-300;
}
</style>
